/**
 * src/components/LeaderboardPanel.tsx
 *
 * Legacy "NEURAL CHAMPIONS" all-time leaderboards panel UI. Full-screen
 * modal, mirrors the GameMenu portal pattern (createPortal to
 * document.body, z-500, ESC-to-close). Tab strip driven by
 * `GamesRegistry.listGames()` so adding a new registered game lights up
 * a new tab automatically without any UI change here.
 *
 * View model:
 *   - GLOBAL tab: top-N by total score points across all leaderboard
 *     entries (summed server-side via v_leaderboard_global_points).
 *   - Per-game tab: top-N rows per metric bucket. time_ms rows show
 *     formatted time + ASC sort; score / rating / win_loss_draw rows
 *     show numeric value + DESC sort.
 *
 * Visual hierarchy: rank-1 row gets a golden Crown badge + warm yellow
 * gradient. Current user's row gets a cyan ring + YOU chip. Empty
 * leaderboards render an explicit "be the first" empty state instead
 * of pretending there's data.
 */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Trophy, X, Crown, Globe, Loader2 } from 'lucide-react';
import { listGames } from '../game/framework/GamesRegistry';
import {
  fetchGlobalPoints,
  fetchPerGame,
  type GlobalPointsRow,
  type PerGameRow,
  type LeaderboardMetric,
} from '../services/leaderboardService';
import { useFocusTrap } from '../hooks/useFocusTrap';

interface LeaderboardPanelProps {
  onClose: () => void;
  currentUserId?: string | null;
}

type Tab = 'global' | string;

interface PerGameGroup {
  metric: LeaderboardMetric;
  rows: PerGameRow[];
}

// Legacy / unwired game ids that show up in GamesRegistry as aliases but
// have no leaderboard writers (no games register these manifests today
// but the type union carries them for back-compat). Filter keeps the
// tab strip honest.
const HIDDEN_GAME_IDS = new Set(['uwu', 'cyber-snake', 'freeplay-chess', 'freedoom']);

function metricOrder(metric: LeaderboardMetric): number {
  switch (metric) {
    case 'score': return 0;
    case 'time_ms': return 1;
    case 'win_loss_draw': return 2;
    case 'rating': return 3;
    default: return 99;
  }
}

function metricLabel(metric: LeaderboardMetric): string {
  // Closed-union switch: each LeaderboardMetric variant returns, no fall-
  // through, no default branch. TypeScript treats this as exhaustive on a
  // closed string-literal union. Adding a new variant to LeaderboardMetric
  // will surface a compile error here — preserving the safety the original
  // switch had (before the never.toUpperCase() bug crept in).
  switch (metric) {
    case 'score': return 'SCORE';
    case 'time_ms': return 'TIME';
    case 'win_loss_draw': return 'WINS';
    case 'rating': return 'RATING';
  }
}

function metricValue(row: PerGameRow): string {
  if (row.metric === 'time_ms' && row.timeMs != null) {
    return formatTime(row.timeMs);
  }
  if (row.score > 0) {
    return row.score.toLocaleString();
  }
  return '—';
}

function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remMin = minutes % 60;
    return `${hours}:${remMin.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return minutes > 0
    ? `${minutes}:${seconds.toString().padStart(2, '0')}`
    : `${seconds}s`;
}

export default function LeaderboardPanel({
  onClose,
  currentUserId,
}: LeaderboardPanelProps) {
  // Focus trap: modal is always rendered via createPortal while mounted.
  const containerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(true, containerRef);

  // Filter to games that (a) are available, (b) declare a leaderboard
  // metric, and (c) aren't legacy aliases. New registers to the
  // GamesRegistry show up here automatically.
  const games = useMemo(
    () =>
      listGames().filter(
        (g) => g.available && g.leaderboard !== null && !HIDDEN_GAME_IDS.has(g.id),
      ),
    [],
  );

  const [tab, setTab] = useState<Tab>('global');
  const [globalRows, setGlobalRows] = useState<GlobalPointsRow[] | null>(null);
  const [perGameRows, setPerGameRows] = useState<PerGameRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch on tab change. Cancellation flag protects against late-setState
  // when the user spams tabs faster than the queries resolve.
  useEffect(() => {
    let cancelled = false;
    setError(null);
    setGlobalRows(null);
    setPerGameRows(null);
    if (tab === 'global') {
      fetchGlobalPoints(50)
        .then((rows) => { if (!cancelled) setGlobalRows(rows); })
        .catch((err: unknown) => {
          if (cancelled) return;
          setError(err instanceof Error ? err.message : String(err));
        });
    } else {
      fetchPerGame(tab, 50)
        .then((rows) => { if (!cancelled) setPerGameRows(rows); })
        .catch((err: unknown) => {
          if (cancelled) return;
          setError(err instanceof Error ? err.message : String(err));
        });
    }
    return () => { cancelled = true; };
  }, [tab]);

  // ESC-to-close. Mirrored from GameMenu.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Identify the current user's rank so the header can show "You are
  // ranked #N" above the table.
  const currentUserRank = useMemo(() => {
    if (!currentUserId) return -1;
    if (tab === 'global' && globalRows) {
      return globalRows.findIndex((r) => r.userId === currentUserId) + 1;
    }
    if (tab !== 'global' && perGameRows) {
      return perGameRows.findIndex((r) => r.userId === currentUserId) + 1;
    }
    return -1;
  }, [tab, globalRows, perGameRows, currentUserId]);

  // Bucket per-game rows by metric for the sectioned display. Sort
  // metric sections in stable order so the UI doesn't reshuffle on
  // re-fetch.
  const perGameGroups = useMemo<PerGameGroup[]>(() => {
    if (!perGameRows) return [];
    const grouped = new Map<LeaderboardMetric, PerGameRow[]>();
    for (const r of perGameRows) {
      const bucket = grouped.get(r.metric) ?? [];
      bucket.push(r);
      grouped.set(r.metric, bucket);
    }
    const out: PerGameGroup[] = [];
    for (const [metric, rows] of grouped) {
      out.push({ metric, rows });
    }
    out.sort((a, b) => metricOrder(a.metric) - metricOrder(b.metric));
    return out;
  }, [perGameRows]);

  return createPortal(
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-label="Leaderboards"
      className="fixed inset-0 z-[500] bg-[#0a0612]/95 backdrop-blur-3xl flex flex-col items-center font-mono text-white overflow-hidden animate-in fade-in duration-200"
    >
      <header className="w-full max-w-5xl px-6 md:px-10 pt-6 md:pt-10 pb-4 flex items-start justify-between border-b border-white/10">
        <div>
          <h1 className="text-3xl md:text-4xl font-black italic tracking-tighter text-yellow-400">
            <span aria-hidden="true">🏆</span> NEURAL_CHAMPIONS.SYS
          </h1>
          <p className="text-[11px] text-white/50 uppercase tracking-widest mt-1">
            All-time points · ranked across every supported game.
          </p>
          {currentUserRank > 0 && (
            <p className="text-[11px] text-cyan-300 uppercase tracking-widest mt-2">
              You are ranked #{currentUserRank}
            </p>
          )}
        </div>
        <button
          onClick={onClose}
          aria-label="Close leaderboards"
          className="shrink-0 px-4 py-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/30 transition-colors text-[11px] font-bold uppercase tracking-widest text-white/70"
        >
          <X size={14} className="inline-block mr-1 -mt-0.5" />
          ESC · Close
        </button>
      </header>

      <nav
        aria-label="Leaderboard games"
        className="w-full max-w-5xl px-6 md:px-10 pt-4 pb-2 flex flex-wrap gap-2 border-b border-white/10"
      >
        <TabChip
          active={tab === 'global'}
          onClick={() => setTab('global')}
          icon={<Globe size={14} />}
          label="GLOBAL"
        />
        {games.map((g) => (
          <TabChip
            key={g.id}
            active={tab === g.id}
            onClick={() => setTab(g.id)}
            icon={g.icon ? <g.icon size={14} /> : <Trophy size={14} />}
            label={g.displayName.toUpperCase()}
          />
        ))}
      </nav>

      <main className="flex-1 w-full max-w-5xl overflow-y-auto px-6 md:px-10 py-6">
        {error && <ErrorState message={error} />}
        {!error && tab === 'global' && (
          globalRows === null ? <Loading /> :
            globalRows.length === 0 ? <EmptyState msg="No scores yet — be the first to play!" /> :
              <GlobalList rows={globalRows} currentUserId={currentUserId} />
        )}
        {!error && tab !== 'global' && (
          perGameRows === null ? <Loading /> :
            perGameRows.length === 0 ? <EmptyState msg="No scores yet for this game." /> :
              <PerGameGroupsList groups={perGameGroups} currentUserId={currentUserId} />
        )}
      </main>

      <footer className="w-full max-w-5xl px-6 md:px-10 py-4 border-t border-white/10 flex items-center justify-between text-[10px] text-white/40 tracking-widest uppercase">
        <span>TOP 50</span>
        <span>
          {tab === 'global'
            ? `${globalRows?.length ?? 0} PLAYERS`
            : `${perGameRows?.length ?? 0} SCORES`}
        </span>
        <span>ESC TO CLOSE</span>
      </footer>
    </div>,
    document.body,
  );
}

function TabChip({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 rounded-xl border text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 transition-colors ${
        active
          ? 'border-yellow-400/40 bg-yellow-500/10 text-yellow-200'
          : 'border-white/10 bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function Loading() {
  return (
    <div className="flex items-center justify-center py-12 gap-2 text-white/50">
      <Loader2 className="animate-spin" size={18} />
      <span className="text-[11px] uppercase tracking-widest">Loading rankings…</span>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="px-4 py-6 border border-red-500/30 bg-red-500/10 rounded-2xl text-red-300 text-sm">
      <strong className="font-bold uppercase tracking-widest text-[11px]">Leaderboard error</strong>
      <p className="mt-2 text-white/70 font-mono">{message}</p>
    </div>
  );
}

function EmptyState({ msg }: { msg: string }) {
  return (
    <div className="py-12 text-center text-white/40 text-sm">
      <Trophy size={32} className="mx-auto mb-3 opacity-30" />
      <p className="uppercase tracking-widest text-[11px]">{msg}</p>
    </div>
  );
}

function RowAvatar({
  photoUrl,
  displayName,
  rank,
}: {
  photoUrl: string | null;
  displayName: string;
  rank: number;
}) {
  const isChampion = rank === 1;
  return (
    <div className="relative shrink-0">
      <div
        className={`h-10 w-10 rounded-full overflow-hidden border-2 flex items-center justify-center text-[11px] font-black uppercase ${
          isChampion
            ? 'border-yellow-400 bg-yellow-500/10'
            : 'border-white/10 bg-white/5'
        }`}
      >
        {photoUrl ? (
          <img src={photoUrl} alt={displayName} className="w-full h-full object-cover" />
        ) : (
          <span>{displayName.slice(0, 2).toUpperCase()}</span>
        )}
      </div>
      {isChampion && (
        <Crown
          size={14}
          className="absolute -top-1.5 -right-1.5 text-yellow-400 drop-shadow-[0_0_6px_rgba(250,204,21,0.6)]"
        />
      )}
    </div>
  );
}

function RankNumber({ rank }: { rank: number }) {
  let textColor = 'text-white/40';
  if (rank === 1) textColor = 'text-yellow-400';
  else if (rank <= 3) textColor = 'text-white/90';
  return (
    <div className={`text-2xl font-black w-10 text-center ${textColor}`}>
      #{rank}
    </div>
  );
}

function RowShell({
  rank,
  isYou,
  isChampion,
  children,
}: {
  rank: number;
  isYou: boolean;
  isChampion: boolean;
  children: React.ReactNode;
}) {
  return (
    <li
      className={`flex items-center gap-4 px-4 py-3 rounded-2xl border transition-colors ${
        isChampion
          ? 'border-yellow-400/50 bg-gradient-to-r from-yellow-500/10 to-transparent'
          : isYou
            ? 'border-cyan-400/40 bg-cyan-500/5'
            : 'border-white/5 bg-white/5'
      }`}
    >
      {children}
    </li>
  );
}

function GlobalList({
  rows,
  currentUserId,
}: {
  rows: GlobalPointsRow[];
  currentUserId?: string | null;
}) {
  return (
    <ol className="flex flex-col gap-1">
      {rows.map((r, i) => {
        const rank = i + 1;
        const isYou = r.userId === currentUserId;
        const isChampion = rank === 1;
        return (
          <RowShell key={r.userId} rank={rank} isYou={isYou} isChampion={isChampion}>
            <RankNumber rank={rank} />
            <RowAvatar photoUrl={r.photoUrl} displayName={r.displayName} rank={rank} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold truncate">
                {r.displayName}{' '}
                {isYou && <span className="text-cyan-300 text-[10px] ml-2 uppercase tracking-widest">YOU</span>}
              </div>
            </div>
            <div className="text-right">
              <div className={`text-xl font-black font-mono ${isChampion ? 'text-yellow-400' : 'text-white'}`}>
                {r.totalPoints.toLocaleString()}
              </div>
              <div className="text-[10px] text-white/40 uppercase tracking-widest">POINTS</div>
            </div>
          </RowShell>
        );
      })}
    </ol>
  );
}

function PerGameGroupsList({
  groups,
  currentUserId,
}: {
  groups: PerGameGroup[];
  currentUserId?: string | null;
}) {
  return (
    <div className="flex flex-col gap-6">
      {groups.map((group) => (
        <section key={group.metric}>
          <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-widest text-white/40">
            <span className="font-bold text-yellow-300">{metricLabel(group.metric)}</span>
            <span aria-hidden="true">·</span>
            <span>{group.metric === 'time_ms' ? 'lower is better' : 'higher is better'}</span>
          </div>
          <ol className="flex flex-col gap-1">
            {group.rows.map((r, i) => {
              const rank = i + 1;
              const isYou = r.userId === currentUserId;
              const isChampion = rank === 1;
              return (
                <RowShell key={`${group.metric}-${r.userId}`} rank={rank} isYou={isYou} isChampion={isChampion}>
                  <RankNumber rank={rank} />
                  <RowAvatar photoUrl={r.photoUrl} displayName={r.displayName} rank={rank} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">
                      {r.displayName}{' '}
                      {isYou && <span className="text-cyan-300 text-[10px] ml-2 uppercase tracking-widest">YOU</span>}
                    </div>
                    {/*
                      Chess (win_loss_draw) rows surface the W/L/D breakdown
                      as a small subtitle line. The full breakdown comes
                      from v_leaderboard_per_game's losses / draws /
                      games_played columns and is populated by
                      CyberChessActivity's recordChessResult call site.
                      We only render the row when at least one of the
                      subtotals is non-zero — fresh users with only wins so
                      far get a clean line.
                    */}
                    {group.metric === 'win_loss_draw' && (
                      (r.losses ?? 0) > 0 ||
                      (r.draws  ?? 0) > 0 ||
                      (r.gamesPlayed ?? 0) > 0
                    ) ? (
                      <div className="text-[10px] text-white/40 mt-0.5 tracking-wider font-mono">
                        <span className="text-emerald-300/80">W {r.score.toLocaleString()}</span>
                        <span className="text-white/30 mx-1">·</span>
                        <span className="text-rose-300/80">L {(r.losses ?? 0).toLocaleString()}</span>
                        <span className="text-white/30 mx-1">·</span>
                        <span className="text-amber-300/80">D {(r.draws ?? 0).toLocaleString()}</span>
                        {(r.gamesPlayed ?? 0) > 0 ? (
                          <>
                            <span className="text-white/30 mx-1">·</span>
                            <span className="text-white/60">{(r.gamesPlayed ?? 0).toLocaleString()} GP</span>
                          </>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  <div className="text-right">
                    <div className={`text-xl font-black font-mono ${isChampion ? 'text-yellow-400' : 'text-white'}`}>
                      {metricValue(r)}
                    </div>
                  </div>
                </RowShell>
              );
            })}
          </ol>
        </section>
      ))}
    </div>
  );
}
